import PropTypes from 'prop-types';
import { generateThumbnail } from '../../../Helpers/ThumbHelper';
import styles from './AppliedProducts.module.css';

// Mapping from step/selection keys to display titles
const STEP_TITLE_MAP = {
    'Roof': 'Roof',
    'Siding': 'Siding',
    'Siding_Profile': 'Siding Profile',
    'Siding_Color': 'Siding Color',
    'SidingAccent': 'Siding Accent',
    'Stone': 'Stone',
    'Brick': 'Brick',
    'ExtDoors': 'Exterior Doors',
    'ExtDoors_Profile': 'Exterior Door Style',
    'ExtDoors_Color': 'Exterior Door Color',
    'ExtTrim': 'Exterior Trim',
    'ExtWindowTrim': 'Exterior Window Trim',
    'Cabinets': 'Cabinets',
    'Cabinets_Door': 'Cabinet Style',
    'Cabinets_Color': 'Cabinet Color',
    'IslandCabinets': 'Island Cabinets',
    'IslandCabinets_Door': 'Island Cabinet Style',
    'IslandCabinets_Color': 'Island Cabinet Color',
    'Counters': 'Countertops',
    'Floor': 'Floor',
    'Backsplash': 'Backsplash',
    'Walls': 'Walls',
    'Trim': 'Trim',
    'WindowTrim': 'Window Trim',
    'CabinetHardware': 'Cabinet Handles',
    'KitchenHardware': 'Hardware',
    'BathHardware': 'Hardware',
    'BathFloor': 'Bath Floor',
    'VanityFloor': 'Vanity Floor',
    'ShowerFloor': 'Shower Floor',
    'ShowerWall': 'Shower Wall',
    'BathWall': 'Bathroom Wall',
    'Carpet': 'Carpet'
};

// Get display title from step name or selection key
const getDisplayTitle = (stepName, selectionKey) => {
    // First try the stepName directly
    if (stepName && STEP_TITLE_MAP[stepName]) {
        return STEP_TITLE_MAP[stepName];
    }
    
    // Try extracting step name from selection key (e.g., "Cabinets_Kitchen_Door" -> "Cabinets_Door")
    if (selectionKey) {
        const parts = selectionKey.split('_');
        if (parts.length >= 3) {
            // Format: StepName_Room_SubType -> StepName_SubType
            const stepWithSubType = `${parts[0]}_${parts[parts.length - 1]}`;
            if (STEP_TITLE_MAP[stepWithSubType]) {
                return STEP_TITLE_MAP[stepWithSubType];
            }
        }
        // Try just the first part
        if (STEP_TITLE_MAP[parts[0]]) {
            return STEP_TITLE_MAP[parts[0]];
        }
    }
    
    // Fallback: format the step name nicely
    const name = stepName || (selectionKey ? selectionKey.split('_')[0] : 'Unknown');
    return name.replace(/([A-Z])/g, ' $1').trim();
};

export default function AppliedProducts({ selections = {}, onClose }) {
    // Convert selections object to array and group by room
    const selectionsList = Object.entries(selections).map(([key, data]) => ({
        key,
        ...data
    }));

    // Group selections by room
    const groupedByRoom = selectionsList.reduce((acc, selection) => {
        const room = selection.room || 'Other';
        if (!acc[room]) {
            acc[room] = [];
        }
        acc[room].push(selection);
        return acc;
    }, {});

    // Sort rooms in a logical order
    const roomOrder = ['Exterior', 'Kitchen', 'Bathroom', 'Vanity', 'Other'];
    const sortedRooms = Object.keys(groupedByRoom).sort((a, b) => {
        const indexA = roomOrder.indexOf(a);
        const indexB = roomOrder.indexOf(b);
        if (indexA === -1 && indexB === -1) return a.localeCompare(b);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
    });

    return (
        <div className={styles.container}>
            {/* Header with navigation */}
            <div className={styles.header}>
                {/* Back Button */}
                <button 
                    className={styles.navButton}
                    onClick={onClose}
                    aria-label="Back to steps"
                >
                    <svg viewBox="0 0 6 10" fill="none" className={styles.navIcon}>
                        <path 
                            d="M5.896907216494845 4.769539078156313 C5.965635738831616 4.836339345357381 6 4.91315965263861 6 5 C6 5.086840347361389 5.965635738831616 5.163660654642618 5.896907216494845 5.230460921843687 L1.092783505154639 9.899799599198397 C1.0240549828178693 9.966599866399466 0.9450171821305842 10 0.8556701030927835 10 C0.766323024054983 10 0.6872852233676976 9.966599866399466 0.6185567010309279 9.899799599198397 L0.10309278350515465 9.39879759519038 C0.0343642611683849 9.331997327989312 0 9.255177020708082 0 9.168336673346694 C0 9.081496325985302 0.0343642611683849 9.004676018704075 0.10309278350515465 8.937875751503006 L4.154639175257731 5 L0.10309278350515465 1.062124248496994 C0.0343642611683849 0.9953239812959247 0 0.9185036740146957 0 0.8316633266533069 C0 0.7448229792919159 0.0343642611683849 0.6680026720106869 0.10309278350515465 0.6012024048096187 L0.6185567010309279 0.1002004008016033 C0.6872852233676976 0.033400133600534065 0.766323024054983 0 0.8556701030927835 0 C0.9450171821305842 0 1.0240549828178693 0.033400133600534065 1.092783505154639 0.1002004008016033 L5.896907216494845 4.769539078156313 Z" 
                            fill="white"
                            transform="scale(-1, 1) translate(-6, 0)"
                        />
                    </svg>
                </button>

                {/* Title */}
                <h2 className={styles.title}>ALL APPLIED</h2>

                {/* Placeholder for symmetry */}
                <div className={styles.navButtonPlaceholder}></div>
            </div>

            {/* Products List grouped by room */}
            {selectionsList.length === 0 ? (
                <div className={styles.emptyMessage}>
                    <p>No products selected yet</p>
                </div>
            ) : (
                <div className={styles.list}>
                    {sortedRooms.map(room => (
                        <div key={room} className={styles.roomSection}>
                            <div className={styles.roomHeader}>
                                <span className={styles.roomTitle}>{room}</span>
                                <div className={styles.roomLine}></div>
                            </div>
                            {groupedByRoom[room].map(({ key, product, stepName }) => {
                                const thumbnailUrl = generateThumbnail(
                                    product.id,
                                    product.specifier,
                                    "256",
                                    500,
                                    product.seriesID,
                                    product.name,
                                    product.color
                                );
                                
                                // Get display title from step name or key
                                const displayName = getDisplayTitle(stepName, key);
                                
                                return (
                                    <div key={key} className={styles.item}>
                                        <div className={styles.imageContainer}>
                                            <img 
                                                src={thumbnailUrl} 
                                                alt={product.color || product.name}
                                                className={styles.image}
                                            />
                                        </div>
                                        
                                        <div className={styles.info}>
                                            <div className={styles.surfaceName}>
                                                {displayName}
                                            </div>
                                            <div className={styles.productName}>
                                                {product.color || product.name || 'Color Name'}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

AppliedProducts.propTypes = {
    selections: PropTypes.object,
    onClose: PropTypes.func
};
